import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Coins, Receipt, CreditCard, TrendUp, TrendDown, Warning, ChartLine,
  Sparkle, CaretDown, CaretUp, Bank, CurrencyDollar,
} from "@phosphor-icons/react";
import { api, formatCurrency } from "@/lib/api";

const TONE = {
  red: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
  amber: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  blue: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
};

function AgingBar({ label, buckets, total, onClick, testid }) {
  const segs = [
    { k: "current", color: "bg-emerald-500", label: "Current" },
    { k: "0_30",    color: "bg-blue-500",    label: "1-30" },
    { k: "31_60",   color: "bg-amber-500",   label: "31-60" },
    { k: "61_90",   color: "bg-orange-500",  label: "61-90" },
    { k: "over_90", color: "bg-red-500",     label: "90+" },
  ];
  return (
    <button onClick={onClick} className="block w-full text-left dc-card p-4 transition hover:shadow-md" data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="overline">{label} Aging</div>
          <div className="mt-1 font-display text-xl font-bold text-slate-900 tabular-nums">{formatCurrency(total)}</div>
        </div>
        <Coins size={16} className="text-amber-500" weight="fill" />
      </div>
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-slate-100" role="progressbar">
        {segs.map((s) => {
          const v = buckets[s.k] || 0;
          const pct = total > 0 ? (v / total) * 100 : 0;
          return pct > 0 ? <div key={s.k} className={s.color} style={{ width: `${pct}%` }} title={`${s.label} · ${formatCurrency(v)}`} /> : null;
        })}
      </div>
      <div className="mt-2 grid grid-cols-5 gap-1 font-mono-alt text-[9px] uppercase tracking-widest text-slate-500">
        {segs.map((s) => (
          <div key={s.k} className="text-center">
            <div className={`inline-block h-1.5 w-1.5 rounded-full ${s.color}`} />
            <div className="mt-0.5">{s.label}</div>
            <div className="tabular-nums text-slate-700">{formatCurrency(buckets[s.k] || 0)}</div>
          </div>
        ))}
      </div>
    </button>
  );
}

function Metric({ label, value, hint, tone = "blue", testid, onClick }) {
  const t = TONE[tone];
  return (
    <button
      onClick={onClick}
      className={`block w-full text-left rounded-lg border ${t.border} ${t.bg} p-3 transition hover:shadow-sm`}
      data-testid={testid}
    >
      <div className="overline">{label}</div>
      <div className={`mt-1 font-display text-lg font-bold tabular-nums ${t.text}`}>{value}</div>
      {hint && <div className="mt-0.5 text-[11px] text-slate-600">{hint}</div>}
    </button>
  );
}

export default function FinanceSmartInsights() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/finance/smart-insights"); setData(data); }
      catch { /* silent */ }
    })();
  }, []);

  const goto = (link) => {
    if (!link) return;
    const lk = link.startsWith("/admin") ? link.slice(6) : link;
    navigate(lk);
  };

  if (!data) {
    return (
      <div className="dc-card p-4 font-mono-alt text-xs uppercase tracking-[0.3em] text-slate-400" data-testid="smart-insights-loading">
        Loading smart insights…
      </div>
    );
  }

  const forecastTone = data.cash_forecast_90d.warning ? "red"
    : data.cash_forecast_90d.projected_end_cash < data.cash_forecast_90d.cash_on_hand * 0.5 ? "amber"
    : "emerald";

  return (
    <section className="space-y-4" data-testid="smart-insights-section">
      <div className="flex items-baseline justify-between">
        <div className="flex items-center gap-2">
          <Sparkle size={16} className="text-[#1E5CBA]" weight="fill" />
          <div>
            <div className="overline">Finance · Operational Brain</div>
            <h2 className="font-display text-lg font-semibold text-slate-900">Smart Insights</h2>
          </div>
        </div>
        <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-400">as of {data.as_of}</span>
      </div>

      {/* Row 1 — Aging buckets */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <AgingBar
          label="AR"
          buckets={data.ar.buckets}
          total={data.ar.total_outstanding}
          onClick={() => goto("/admin/finance?tab=ar")}
          testid="ar-aging-bar"
        />
        <AgingBar
          label="AP"
          buckets={data.ap.buckets}
          total={data.ap.total_outstanding}
          onClick={() => goto("/admin/finance?tab=ap")}
          testid="ap-aging-bar"
        />
      </div>

      {/* Row 2 — DSO / DPO / Cash forecast metrics */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Metric
          label="DSO (days)" value={`${data.ar.dso_days}d`}
          hint="Days Sales Outstanding"
          tone={data.ar.dso_days > 60 ? "red" : data.ar.dso_days > 30 ? "amber" : "emerald"}
          testid="kpi-dso"
          onClick={() => goto("/admin/finance?tab=ar")}
        />
        <Metric
          label="DPO (days)" value={`${data.ap.dpo_days}d`}
          hint="Days Payables Outstanding"
          tone={data.ap.dpo_days > 90 ? "red" : data.ap.dpo_days > 45 ? "amber" : "emerald"}
          testid="kpi-dpo"
          onClick={() => goto("/admin/finance?tab=ap")}
        />
        <Metric
          label="Cash on hand" value={formatCurrency(data.cash_forecast_90d.cash_on_hand)}
          hint="All banks + petty cash"
          tone="blue"
          testid="kpi-cash"
          onClick={() => goto("/admin/finance?tab=bank_recon")}
        />
        <Metric
          label="90d end cash" value={formatCurrency(data.cash_forecast_90d.projected_end_cash)}
          hint={`Min point: ${formatCurrency(data.cash_forecast_90d.min_cash_point_est)}`}
          tone={forecastTone}
          testid="kpi-forecast-end"
          onClick={() => goto("/admin/finance?tab=cashflow")}
        />
        <Metric
          label="Net flow 90d"
          value={formatCurrency(data.cash_forecast_90d.projected_inflow - data.cash_forecast_90d.projected_outflow)}
          hint={`In ${formatCurrency(data.cash_forecast_90d.projected_inflow)} · Out ${formatCurrency(data.cash_forecast_90d.projected_outflow)}`}
          tone={data.cash_forecast_90d.projected_inflow >= data.cash_forecast_90d.projected_outflow ? "emerald" : "red"}
          testid="kpi-net-flow"
          onClick={() => goto("/admin/finance?tab=cashflow")}
        />
      </div>

      {/* Row 3 — Top clients / vendors (collapsible) */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="dc-card overflow-hidden" data-testid="top-clients-card">
          <button
            onClick={() => setExpanded(expanded === "clients" ? null : "clients")}
            className="flex w-full items-center justify-between border-b border-slate-100 bg-blue-50/60 px-4 py-2 text-left hover:bg-blue-50"
          >
            <div className="flex items-center gap-2">
              <TrendUp size={14} className="text-blue-600" weight="fill" />
              <span className="font-display text-sm font-semibold text-slate-900">Top receivables — by client</span>
            </div>
            {expanded === "clients" ? <CaretUp size={12} /> : <CaretDown size={12} />}
          </button>
          {expanded === "clients" && (
            <ul className="divide-y divide-slate-100" data-testid="top-clients-list">
              {data.ar.top_clients.length === 0 && <li className="px-4 py-4 text-xs text-slate-500">No outstanding receivables.</li>}
              {data.ar.top_clients.map((c, i) => (
                <li key={c.name + i}>
                  <button
                    onClick={() => goto("/admin/finance?tab=ar")}
                    className="flex w-full items-center gap-3 px-4 py-2 text-left text-xs hover:bg-slate-50"
                    data-testid={`top-client-${i}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-semibold text-slate-900">{c.name}</div>
                      <div className="text-[11px] text-slate-500">
                        {c.invoice_count} invoice{c.invoice_count === 1 ? "" : "s"}
                        {c.days_overdue > 0 && <> · <span className={c.days_overdue > 60 ? "text-red-600" : "text-amber-600"}>{c.days_overdue}d overdue</span></>}
                      </div>
                    </div>
                    <div className="font-mono-alt tabular-nums font-semibold text-slate-900">{formatCurrency(c.outstanding)}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="dc-card overflow-hidden" data-testid="top-vendors-card">
          <button
            onClick={() => setExpanded(expanded === "vendors" ? null : "vendors")}
            className="flex w-full items-center justify-between border-b border-slate-100 bg-red-50/60 px-4 py-2 text-left hover:bg-red-50"
          >
            <div className="flex items-center gap-2">
              <TrendDown size={14} className="text-red-600" weight="fill" />
              <span className="font-display text-sm font-semibold text-slate-900">Top payables — by vendor</span>
            </div>
            {expanded === "vendors" ? <CaretUp size={12} /> : <CaretDown size={12} />}
          </button>
          {expanded === "vendors" && (
            <ul className="divide-y divide-slate-100" data-testid="top-vendors-list">
              {data.ap.top_vendors.length === 0 && <li className="px-4 py-4 text-xs text-slate-500">No outstanding payables.</li>}
              {data.ap.top_vendors.map((v, i) => (
                <li key={v.name + i}>
                  <button
                    onClick={() => goto("/admin/finance?tab=ap")}
                    className="flex w-full items-center gap-3 px-4 py-2 text-left text-xs hover:bg-slate-50"
                    data-testid={`top-vendor-${i}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-semibold text-slate-900">{v.name}</div>
                      <div className="text-[11px] text-slate-500">
                        {v.bill_count} bill{v.bill_count === 1 ? "" : "s"}
                        {v.days_overdue > 0 && <> · <span className={v.days_overdue > 60 ? "text-red-600" : "text-amber-600"}>{v.days_overdue}d overdue</span></>}
                      </div>
                    </div>
                    <div className="font-mono-alt tabular-nums font-semibold text-slate-900">{formatCurrency(v.outstanding)}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Row 4 — Alerts: anomalies · budget variance · tax · unreconciled */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
        <AlertCard
          testid="alert-anomalies"
          icon={Sparkle} iconTone="text-amber-600"
          title="Anomaly flags"
          count={data.anomalies.length}
          items={data.anomalies.map((a) => ({ ...a, subtitle_override: a.subtitle }))}
          emptyText="No unusual invoices or bills detected."
          tone={data.anomalies.length ? "amber" : "emerald"}
          onItemClick={goto}
        />
        <AlertCard
          testid="alert-budget-variance"
          icon={ChartLine} iconTone="text-red-600"
          title="Budget over-run"
          count={data.budget_variances.length}
          items={data.budget_variances.map((b) => ({
            id: b.id, title: b.name,
            subtitle_override: `Actual ${formatCurrency(b.actual)} vs budget ${formatCurrency(b.budget)} · +${b.variance_pct}%`,
            link: b.link,
            severity: b.severity,
          }))}
          emptyText="All budgets within ±10%."
          tone={data.budget_variances.length ? "red" : "emerald"}
          onItemClick={goto}
        />
        <AlertCard
          testid="alert-tax"
          icon={CurrencyDollar} iconTone="text-blue-600"
          title="Tax due · 30d"
          count={data.tax_upcoming_30d.length}
          items={data.tax_upcoming_30d.map((t) => ({
            id: t.id,
            title: `${(t.tax_type || "").replace(/_/g, " ").toUpperCase()} · ${t.period}`,
            subtitle_override: `${formatCurrency(t.liability)} due ${t.due_date}`,
            link: "/admin/finance?tab=tax",
          }))}
          emptyText="No tax obligations in next 30 days."
          tone={data.tax_upcoming_30d.length ? "amber" : "emerald"}
          onItemClick={goto}
        />
        <AlertCard
          testid="alert-unreconciled"
          icon={Bank} iconTone="text-amber-600"
          title="Banks unreconciled"
          count={data.unreconciled_banks.length}
          items={data.unreconciled_banks.map((b) => ({
            id: b.bank_key,
            title: b.bank_key.charAt(0).toUpperCase() + b.bank_key.slice(1) + " Bank",
            subtitle_override: b.last_period
              ? `Last ${b.last_period} · ${b.status.replace(/_/g, " ")}`
              : "Never reconciled",
            link: "/admin/finance?tab=bank_recon",
          }))}
          emptyText="All banks reconciled up to last period."
          tone={data.unreconciled_banks.length ? "amber" : "emerald"}
          onItemClick={goto}
        />
      </div>
    </section>
  );
}

function AlertCard({ testid, icon: Icon, iconTone, title, count, items, emptyText, tone, onItemClick }) {
  const [open, setOpen] = useState(false);
  const t = TONE[tone] || TONE.blue;
  return (
    <div className={`rounded-lg border ${t.border} ${t.bg} overflow-hidden`} data-testid={testid}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-start justify-between px-3 py-2.5"
      >
        <div className="flex items-start gap-2">
          <Icon size={16} className={iconTone} weight="fill" />
          <div className="text-left">
            <div className="font-display text-sm font-semibold text-slate-900">{title}</div>
            <div className={`font-display text-2xl font-bold tabular-nums ${t.text}`}>{count}</div>
          </div>
        </div>
        {count > 0 && (open ? <CaretUp size={12} /> : <CaretDown size={12} />)}
      </button>
      {open && (
        <ul className="border-t border-white/60 bg-white divide-y divide-slate-100 max-h-60 overflow-y-auto">
          {count === 0 && <li className="px-3 py-3 text-xs text-slate-500">{emptyText}</li>}
          {items.map((it, i) => (
            <li key={`${it.id}-${i}`}>
              <button
                onClick={() => onItemClick(it.link)}
                className="flex w-full items-start gap-2 px-3 py-2 text-left text-xs hover:bg-slate-50"
                data-testid={`${testid}-item-${i}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold text-slate-900">{it.title}</div>
                  <div className="text-[11px] text-slate-500">{it.subtitle_override || it.subtitle}</div>
                </div>
                <Warning size={10} className={it.severity === "critical" ? "text-red-500 mt-0.5" : "text-amber-500 mt-0.5"} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
